import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, useEffect, useState } from 'react';
import { ApiException, GroupBusinessSettings } from '../../../../../types';
import { useForm, Controller } from 'react-hook-form';
import { handleErrors } from '../../../../utils/errors';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import {
  UserKeys,
  saveGroupSettings,
  saveGroupSettingsUserPortal,
} from '../../../../api/users-api';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { emailRegex } from '../../../../utils/regular-expressions';
import { useUserSettings } from '../../../../utils/use-user-settings';
import axios from 'axios';
import { isFeatureEnabled } from '../../../../utils/feature-flags';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button';
import SharedButton from '../../../../shared/buttons/shared-button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface GroupSettingsFormProps {
  groupSettings: GroupBusinessSettings;
  groupEmail: string;
  domain: string;
  directoryRoles: string[];
  changeLogRoles: string[];
  userView: boolean;
  isUserMember: boolean;
  isUserManager: boolean;
  isUserOwner: boolean;
}

export default function GroupSettingsForm({
  domain,
  directoryRoles,
  groupSettings,
  groupEmail,
  changeLogRoles,
  userView,
  isUserMember,
  isUserManager,
  isUserOwner,
}: GroupSettingsFormProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [enableDisableGroupClicked, setEnableDisableGroupClicked] =
    useState(false);
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const queryClient = useQueryClient();
  const { currentTier, features } = useUserSettings();
  const { mutate, isPending: updatingGroupSettings } = useMutation({
    mutationFn: (data: GroupBusinessSettings) =>
      saveGroupSettings(groupEmail, data, groupSettings, changeLogMessage),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Group settings updated successfully!',
      });

      queryClient.setQueryData([UserKeys.GroupSettings, groupEmail], data);
      setChangeLogMessage('');
      if (
        currentTier === 'enterprise' &&
        isFeatureEnabled('indexGroupsReact', features)
      ) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.GroupsIndexedPage],
        });
      }
      reset();
    },
    onError: (error) => {
      groupSettingErrorCheck(error);
    },
    onSettled: () => {
      setEnableDisableGroupClicked(true);
      createEvent(
        analytics.directory.group_details.update_group_business_settings,
        {
          module: ModuleList.modules.directory.name,
          section: ModuleList.modules.directory.sections.groups,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  const groupSettingErrorCheck = (error: Error) => {
    if (axios.isAxiosError(error)) {
      const errorResponse = error.response?.data as ApiException;
      if (
        errorResponse.message?.includes(
          'Cannot allow external members to join group.'
        )
      ) {
        setFormError('whoCanJoin', {
          type: 'manual',
          message:
            'Group administrator has disallowed external members to join group.',
        });
      } else if (
        errorResponse.message?.includes(
          'Cannot allow external members to view group content'
        )
      ) {
        setFormError('whoCanViewGroup', {
          type: 'manual',
          message:
            'Group administrator has disallowed external members to view group content.',
        });
      } else {
        handleErrors({
          error,
          snackbarMessage:
            'There was an issue updating the group settings. Please try again.',
        });
      }
    } else {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue updating the group settings. Please try again.',
      });
    }
  };

  const {
    mutate: userPortalMutate,
    isPending: updatingGroupSettingsUserPortal,
  } = useMutation({
    mutationFn: (data: GroupBusinessSettings) =>
      saveGroupSettingsUserPortal(groupEmail, data, groupSettings),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Group settings updated successfully!',
      });

      queryClient.setQueryData([UserKeys.GroupSettings, groupEmail], data);
      reset();
    },
    onError: (error) => {
      groupSettingErrorCheck(error);
    },
    onSettled: () => {
      setEnableDisableGroupClicked(true);
      createEvent(
        analytics.userView.group_details.update_group_business_settings,
        {
          module: ModuleList.modules.userView.name,
          section: ModuleList.modules.userView.sections.groupDetails,
        }
      );
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState,
    getValues,
    watch,
    setValue,
    setError: setFormError,
  } = useForm({
    values: {
      allowWebPosting: groupSettings.allowWebPosting,
      primaryLanguage: groupSettings.primaryLanguage,
      includeInGlobalAddressList: groupSettings.includeInGlobalAddressList,
      isArchived: groupSettings.isArchived,
      messageModerationLevel: groupSettings.messageModerationLevel,
      sendMessageDenyNotification: groupSettings.sendMessageDenyNotification,
      spamModerationLevel: groupSettings.spamModerationLevel,
      replyTo: groupSettings.replyTo,
      defaultMessageDenyNotificationText:
        groupSettings.defaultMessageDenyNotificationText,
      whoCanViewGroup: groupSettings.whoCanViewGroup,
      whoCanPostMessage: groupSettings.whoCanPostMessage,
      whoCanViewMembership: groupSettings.whoCanViewMembership,
      whoCanModerateMembers: groupSettings.whoCanModerateMembers,
      whoCanModerateContent: groupSettings.whoCanModerateContent,
      whoCanAssistContent: groupSettings.whoCanAssistContent,
      whoCanDiscoverGroup: groupSettings.whoCanDiscoverGroup,
      allowExternalMembers: groupSettings.allowExternalMembers,
      whoCanJoin: groupSettings.whoCanJoin,
      whoCanLeaveGroup: groupSettings.whoCanLeaveGroup,
      whoCanContactOwner: groupSettings.whoCanContactOwner,
      archiveOnly: groupSettings.archiveOnly,
      membersCanPostAsTheGroup: groupSettings.membersCanPostAsTheGroup,
      customReplyTo: groupSettings.customReplyTo,
    },
  });
  const { isDirty, dirtyFields, errors } = formState;

  useEffect(() => {
    if (!Object.keys(errors)[0]) return;
    const firstErrorField = Object.keys(errors)[0];
    if (firstErrorField) {
      const errorElement = document.querySelector(`[name=${firstErrorField}]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [formState]);

  const onSubmit = () => {
    if (
      (!userView &&
        dirtyFields.primaryLanguage &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditGeneralInfoSection.value
        )) ||
      (!userView &&
        dirtyFields.allowWebPosting &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditGeneralInfoSection.value
        )) ||
      (!userView &&
        dirtyFields.includeInGlobalAddressList &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditDirectorySection.value
        )) ||
      (!userView &&
        dirtyFields.isArchived &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditContentControlSection.value
        )) ||
      (!userView &&
        dirtyFields.membersCanPostAsTheGroup &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditSettingsSection.value
        )) ||
      (!userView &&
        dirtyFields.messageModerationLevel &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditSettingsSection.value
        )) ||
      (!userView &&
        dirtyFields.sendMessageDenyNotification &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditSettingsSection.value
        )) ||
      (!userView &&
        dirtyFields.defaultMessageDenyNotificationText &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditSettingsSection.value
        )) ||
      (!userView &&
        dirtyFields.spamModerationLevel &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditSettingsSection.value
        )) ||
      (!userView &&
        dirtyFields.replyTo &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditSettingsSection.value
        )) ||
      (!userView &&
        dirtyFields.whoCanViewGroup &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditPermissionsSection.value
        )) ||
      (!userView &&
        dirtyFields.whoCanPostMessage &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditPermissionsSection.value
        )) ||
      (!userView &&
        dirtyFields.whoCanViewMembership &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditPermissionsSection.value
        )) ||
      (!userView &&
        dirtyFields.whoCanModerateMembers &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditPermissionsSection.value
        )) ||
      (!userView &&
        dirtyFields.whoCanModerateContent &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditPermissionsSection.value
        )) ||
      (!userView &&
        dirtyFields.whoCanAssistContent &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditPermissionsSection.value
        )) ||
      (!userView &&
        dirtyFields.whoCanJoin &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditPermissionsSection.value
        )) ||
      (!userView &&
        dirtyFields.allowExternalMembers &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditPermissionsSection.value
        )) ||
      (!userView &&
        dirtyFields.whoCanLeaveGroup &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditPermissionsSection.value
        )) ||
      (!userView &&
        dirtyFields.whoCanContactOwner &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditPermissionsSection.value
        )) ||
      (!userView &&
        dirtyFields.whoCanDiscoverGroup &&
        isAbleToSeeUIElement(
          changeLogRoles,
          DirectoryRoles.EditPermissionsSection.value
        ))
    ) {
      setOpenChangeLogDialog(true);
    } else if (userView) {
      const newSettings: GroupBusinessSettings = {
        ...groupSettings,
        ...getValues(),
      };
      userPortalMutate(newSettings);
    } else {
      const newSettings: GroupBusinessSettings = {
        ...groupSettings,
        ...getValues(),
      };
      mutate(newSettings);
    }
  };

  const archiveOnly = watch('archiveOnly');

  const primaryLanguageOptions = [
    { displayValue: 'Arabic', value: 'Arabic' },
    { displayValue: 'Bengali', value: 'Bengali' },
    { displayValue: 'Bulgarian', value: 'Bulgarian' },
    { displayValue: 'Catalan', value: 'Catalan' },
    { displayValue: 'Chinese - Simplified', value: 'Chinese_Simplified' },
    { displayValue: 'Chinese - Traditional', value: 'Chinese_Traditional' },
    { displayValue: 'Croatian', value: 'Croatian' },
    { displayValue: 'Czech', value: 'Czech' },
    { displayValue: 'Danish', value: 'Danish' },
    { displayValue: 'Dutch', value: 'Dutch' },
    { displayValue: 'English - United States', value: 'English_United_States' },
    {
      displayValue: 'English - United Kingdom',
      value: 'English_United_Kingdom',
    },
    { displayValue: 'Estonian', value: 'Estonian' },
    { displayValue: 'Finnish', value: 'Finnish' },
    { displayValue: 'French', value: 'French' },
    { displayValue: 'German', value: 'German' },
    { displayValue: 'Greek', value: 'Greek' },
    { displayValue: 'Gujarati', value: 'Gujarati' },
    { displayValue: 'Hebrew', value: 'Hebrew' },
    { displayValue: 'Hindi', value: 'Hindi' },
    { displayValue: 'Hungarian', value: 'Hungarian' },
    { displayValue: 'Icelandic', value: 'Icelandic' },
    { displayValue: 'Indonesian', value: 'Indonesian' },
    { displayValue: 'Italian', value: 'Italian' },
    { displayValue: 'Japanese', value: 'Japanese' },
    { displayValue: 'Kannada', value: 'Kannada' },
    { displayValue: 'Korean', value: 'Korean' },
    { displayValue: 'Latvian', value: 'Latvian' },
    { displayValue: 'Lithuanian', value: 'Lituanian' },
    { displayValue: 'Malay', value: 'Malay' },
    { displayValue: 'Malayalam', value: 'Malayalam' },
    { displayValue: 'Marathi', value: 'Marathi' },
    { displayValue: 'Norwegian', value: 'Norewegian' },
    { displayValue: 'Oriya', value: 'Oriya' },
    { displayValue: 'Persian', value: 'Persian' },
    { displayValue: 'Polish', value: 'Polish' },
    { displayValue: 'Portuguese - Brazil', value: 'Portuguese_Brazil' },
    { displayValue: 'Portuguese - Portugal', value: 'Portuguese_Portugal' },
    { displayValue: 'Romanian', value: 'Romanian' },
    { displayValue: 'Russian', value: 'Russian' },
    { displayValue: 'Serbian', value: 'Serbian' },
    { displayValue: 'Slovak', value: 'Slovak' },
    { displayValue: 'Slovenian', value: 'Slovenian' },
    { displayValue: 'Spanish', value: 'Spanish' },
    { displayValue: 'Swedish', value: 'Swedish' },
    { displayValue: 'Tagalog', value: 'Tagalog' },
    { displayValue: 'Tamil', value: 'Tamil' },
    { displayValue: 'Telugu', value: 'Telugu' },
    { displayValue: 'Thai', value: 'Thai' },
    { displayValue: 'Turkish', value: 'Turkish' },
    { displayValue: 'Ukrainian', value: 'Ukranian' },
    { displayValue: 'Urdu', value: 'Urdu' },
    { displayValue: 'Vietnamese', value: 'Vietnamese' },
  ];

  const moderationLevelOptions = [
    { displayValue: 'Moderate all messages', value: 'MODERATE_ALL_MESSAGES' },
    {
      displayValue: 'Moderate messages by new members',
      value: 'MODERATE_NEW_MEMBERS',
    },
    {
      displayValue: 'Moderate messages by non members',
      value: 'MODERATE_NON_MEMBERS',
    },
    { displayValue: `Don't moderate messages`, value: 'MODERATE_NONE' },
  ];

  const spamModerationLevelOptions = [
    {
      displayValue:
        'Send them to the moderation queue and send notification to moderators',
      value: 'MODERATE',
    },
    {
      displayValue:
        'Send them to the moderation queue, but do not send notification to the moderators',
      value: 'SILENTLY_MODERATE',
    },
    { displayValue: 'Immediately reject them', value: 'REJECT' },
    {
      displayValue: 'Skip the moderation queue and post to the group',
      value: 'ALLOW',
    },
  ];

  const replyToOptions = [
    { displayValue: 'To the entire group', value: 'REPLY_TO_LIST' },
    {
      displayValue: 'To the author of the message only',
      value: 'REPLY_TO_SENDER',
    },
    { displayValue: 'To the owners of the group', value: 'REPLY_TO_OWNER' },
    {
      displayValue: 'To the managers of the group',
      value: 'REPLY_TO_MANAGERS',
    },
    {
      displayValue: 'Users decide where their replies are sent',
      value: 'REPLY_TO_IGNORE',
    },
    {
      displayValue: 'Use a custom address to send replies to',
      value: 'REPLY_TO_CUSTOM',
    },
  ];

  const whoCanViewGroupOptions = [
    { displayValue: 'Group owners', value: 'ALL_OWNERS_CAN_VIEW' },
    { displayValue: 'Group managers', value: 'ALL_MANAGERS_CAN_VIEW' },
    { displayValue: 'Group members', value: 'ALL_MEMBERS_CAN_VIEW' },
    { displayValue: 'Entire organization', value: 'ALL_IN_DOMAIN_CAN_VIEW' },
    { displayValue: 'Anyone on the web', value: 'ANYONE_CAN_VIEW' },
  ];

  const whoCanPostMessageOptions = archiveOnly
    ? [{ displayValue: 'None can post', value: 'NONE_CAN_POST' }]
    : [
        { displayValue: 'Group owners', value: 'ALL_OWNERS_CAN_POST' },
        { displayValue: 'Group managers', value: 'ALL_MANAGERS_CAN_POST' },
        { displayValue: 'Group members', value: 'ALL_MEMBERS_CAN_POST' },
        {
          displayValue: 'Entire organization',
          value: 'ALL_IN_DOMAIN_CAN_POST',
        },
        { displayValue: 'Anyone on the web', value: 'ANYONE_CAN_POST' },
      ];

  const whoCanViewMembersOptions = [
    { displayValue: 'Group Owners', value: 'ALL_OWNERS_CAN_VIEW' },
    { displayValue: 'Group managers', value: 'ALL_MANAGERS_CAN_VIEW' },
    { displayValue: 'Group members', value: 'ALL_MEMBERS_CAN_VIEW' },
    { displayValue: 'Entire organization', value: 'ALL_IN_DOMAIN_CAN_VIEW' },
  ];
  // whoCanViewMembership > whoCanModerateMembers
  const whoCanModerateMembersOptions = [
    { displayValue: 'Group Owners', value: 'OWNERS_ONLY' },
    { displayValue: 'Group managers', value: 'OWNERS_AND_MANAGERS' },
    { displayValue: 'Group members', value: 'ALL_MEMBERS' },
    // theres supposed to be an "entire organization" choice here
    // but its not in any of googles api or documentation
    { displayValue: 'None', value: 'NONE' },
  ];

  const whoCanModerateContentOptions = [
    { displayValue: 'Group Owners', value: 'OWNERS_ONLY' },
    { displayValue: 'Group managers', value: 'OWNERS_AND_MANAGERS' },
    { displayValue: 'Group members', value: 'ALL_MEMBERS' },
    { displayValue: 'None', value: 'NONE' },
  ];

  const whoCanAssistContentOptions = [
    { displayValue: 'Group Owners', value: 'OWNERS_ONLY' },
    { displayValue: 'Group managers', value: 'OWNERS_AND_MANAGERS' },
    { displayValue: 'Group members', value: 'ALL_MEMBERS' },
    { displayValue: 'None', value: 'NONE' },
  ];

  const whoCanLeaveGroupOptions = [
    { displayValue: 'Group Owners', value: 'ALL_OWNERS_CAN_LEAVE' },
    { displayValue: 'Group managers', value: 'ALL_MANAGERS_CAN_LEAVE' },
    { displayValue: 'Group members', value: 'ALL_MEMBERS_CAN_LEAVE' },
    { displayValue: 'None', value: 'NONE_CAN_LEAVE' },
  ];

  const inviteMembersOptions = [
    { displayValue: 'Anyone on the web can join', value: 'ANYONE_CAN_JOIN' },
    {
      displayValue: 'Organization users only',
      value: 'ALL_IN_DOMAIN_CAN_JOIN',
    },
    {
      displayValue: 'Organization users can ask',
      value: 'CAN_REQUEST_TO_JOIN',
    },
    { displayValue: 'Invited users only', value: 'INVITED_CAN_JOIN' },
  ];

  const whoCanContactOwnerOptions = [
    { displayValue: 'Group managers', value: 'ALL_MANAGERS_CAN_CONTACT' },
    { displayValue: 'Group members', value: 'ALL_MEMBERS_CAN_CONTACT' },
    { displayValue: 'Entire organization', value: 'ALL_IN_DOMAIN_CAN_CONTACT' },
    { displayValue: 'Anyone on the web', value: 'ANYONE_CAN_CONTACT' },
  ];

  // whoCanViewMembership > whoCanDiscoverGroupOptions
  const whoCanDiscoverGroupOptions = [
    { displayValue: 'Group members', value: 'ALL_MEMBERS_CAN_DISCOVER' },
    {
      displayValue: 'Entire organization',
      value: 'ALL_IN_DOMAIN_CAN_DISCOVER',
    },
    { displayValue: 'Anyone on the web', value: 'ANYONE_CAN_DISCOVER' },
  ];

  const whoCanViewMembershipValue = watch('whoCanViewMembership');

  const getFilteredModerateOptions = (viewValue: string) => {
    switch (viewValue) {
      case 'ALL_IN_DOMAIN_CAN_VIEW':
      case 'ALL_MEMBERS_CAN_VIEW':
        return whoCanModerateMembersOptions;
      case 'ALL_MANAGERS_CAN_VIEW':
        return whoCanModerateMembersOptions.filter((option) =>
          ['OWNERS_ONLY', 'OWNERS_AND_MANAGERS', 'NONE'].includes(option.value)
        );
      case 'ALL_OWNERS_CAN_VIEW':
        return whoCanModerateMembersOptions.filter((option) =>
          ['OWNERS_ONLY', 'NONE'].includes(option.value)
        );
      default:
        return [];
    }
  };

  const getFilteredDiscoverOptions = (viewValue: string) => {
    switch (viewValue) {
      case 'ALL_IN_DOMAIN_CAN_VIEW':
        return whoCanDiscoverGroupOptions.filter((option) =>
          ['ALL_IN_DOMAIN_CAN_DISCOVER', 'ANYONE_CAN_DISCOVER'].includes(
            option.value
          )
        );
      default:
        return whoCanDiscoverGroupOptions;
    }
  };

  const whoCanViewMemberOnchange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newWhoCanViewMembership = e.target.value;
    // autoselect valid who can manage members
    const currentWhoCanModerateMembers = getValues('whoCanModerateMembers');

    const filteredOptions = getFilteredModerateOptions(newWhoCanViewMembership);

    const isCurrentValid = filteredOptions.some(
      (option) => option.value === currentWhoCanModerateMembers
    );

    if (!isCurrentValid && filteredOptions.length > 0) {
      const fullIndex = whoCanModerateMembersOptions.findIndex(
        (option) => option.value === currentWhoCanModerateMembers
      );
      const nextValid = filteredOptions.find(
        (option) =>
          whoCanModerateMembersOptions.findIndex(
            (o) => o.value === option.value
          ) > fullIndex
      );
      const newValue = (nextValid ?? filteredOptions[0]).value as
        | 'ALL_MEMBERS'
        | 'OWNERS_AND_MANAGERS'
        | 'OWNERS_ONLY'
        | 'NONE';
      setValue('whoCanModerateMembers', newValue);
    }

    // autoselect valid who can discover group
    const currentWhoCanDiscoverGroup = getValues('whoCanDiscoverGroup');

    const filteredDiscoverOptions = getFilteredDiscoverOptions(
      newWhoCanViewMembership
    );
    const isCurrentDiscoverValid = filteredDiscoverOptions.some(
      (option) => option.value === currentWhoCanDiscoverGroup
    );
    if (!isCurrentDiscoverValid && filteredDiscoverOptions.length > 0) {
      const fullDiscoverIndex = whoCanDiscoverGroupOptions.findIndex(
        (option) => option.value === currentWhoCanDiscoverGroup
      );
      const nextDisoverValid = filteredDiscoverOptions.find(
        (option) =>
          whoCanDiscoverGroupOptions.findIndex(
            (o) => o.value === option.value
          ) > fullDiscoverIndex
      );
      const newDiscoverValue = (nextDisoverValid ?? filteredDiscoverOptions[0])
        .value as
        | 'ANYONE_CAN_DISCOVER'
        | 'ALL_IN_DOMAIN_CAN_DISCOVER'
        | 'ALL_MEMBERS_CAN_DISCOVER';
      setValue('whoCanDiscoverGroup', newDiscoverValue);
    }
  };

  return !userView ||
    (userView && isUserOwner) ||
    (userView && isUserMember) ||
    (userView && isUserManager) ? (
    <Box
      component="form"
      sx={{
        position: 'relative',
        height: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
      onSubmit={handleSubmit(onSubmit)}
    >
      {/* Action Buttons */}
      <Stack
        direction="row"
        spacing={1}
        sx={{ position: 'absolute', top: -60, right: 0 }}
      >
        <SharedIconButton
          variant="refresh"
          tooltipLabel="Refresh"
          onClick={() => {
            void queryClient.invalidateQueries({
              queryKey: [UserKeys.GroupSettings, groupEmail],
            });
          }}
        />
        <SharedButton
          variant="primary"
          disabled={
            !isDirty ||
            updatingGroupSettings ||
            updatingGroupSettingsUserPortal ||
            isUserMember
          }
          loading={updatingGroupSettings || updatingGroupSettingsUserPortal}
          type="submit"
        >
          Save
        </SharedButton>
      </Stack>
      <Stack
        sx={{ flex: 1, margin: '8px 0', height: '100%', overflow: 'auto' }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
          General Information
        </Typography>

        <Typography variant="body2" sx={{ fontWeight: 300, pt: 2, pl: 4 }}>
          This is the language that emails from the Google groups service will
          be sent in. For example: digest emails, email footers, etc.
        </Typography>
        <Stack flexDirection="row" sx={{ py: 2, px: 4 }} gap={14}>
          <Controller
            name="primaryLanguage"
            control={control}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'primaryLanguage',
                }}
                InputLabelProps={{
                  htmlFor: 'primaryLanguage',
                }}
                select
                disabled={
                  (!userView &&
                    !isAbleToSeeUIElement(
                      directoryRoles,
                      DirectoryRoles.EditGeneralInfoSection.value
                    )) ||
                  (userView && isUserMember)
                }
                label="Primary Language"
                sx={{ width: 400 }}
                value={field.value || ''}
                onChange={field.onChange}
              >
                {primaryLanguageOptions.map((option) => (
                  <MenuItem value={option.value} key={option.displayValue}>
                    {option.displayValue}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="allowWebPosting"
            control={control}
            render={({ field: props }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...props}
                    disabled={
                      (!userView &&
                        !isAbleToSeeUIElement(
                          directoryRoles,
                          DirectoryRoles.EditGeneralInfoSection.value
                        )) ||
                      (userView && isUserMember)
                    }
                    checked={props.value}
                    onChange={(e) => props.onChange(e.target.checked)}
                  />
                }
                label="Allow users to post to the group on the web"
              />
            )}
          />
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
          Directory
        </Typography>
        <Stack sx={{ py: 2, px: 4 }} gap={1}>
          <Typography variant="body2" sx={{ fontWeight: 300 }}>
            This option includes the group in your Google Apps Global Address
            List. When included in the list, the group becomes available to
            Google Apps Sync for Microsoft Outlook users.
          </Typography>
          <Controller
            name="includeInGlobalAddressList"
            control={control}
            render={({ field: props }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...props}
                    disabled={
                      (!userView &&
                        !isAbleToSeeUIElement(
                          directoryRoles,
                          DirectoryRoles.EditDirectorySection.value
                        )) ||
                      (userView && isUserMember)
                    }
                    checked={props.value}
                    onChange={(e) => props.onChange(e.target.checked)}
                  />
                }
                label="Include this group in Global Address Lists"
              />
            )}
          />
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
          Content Control
        </Typography>
        <Stack
          sx={{
            py: 2,
            px: 4,
          }}
          gap={1}
        >
          <Typography variant="body2" sx={{ fontWeight: 300 }}>
            When this option is unchecked, messages sent to the group will not
            be archived. Previously received messages will remain archived. Only
            archived messages are visible through the web interface.
          </Typography>
          <Controller
            name="isArchived"
            control={control}
            render={({ field: props }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    {...props}
                    checked={props.value}
                    disabled={
                      (!userView &&
                        !isAbleToSeeUIElement(
                          directoryRoles,
                          DirectoryRoles.EditContentControlSection.value
                        )) ||
                      (userView && isUserMember)
                    }
                    onChange={(e) => props.onChange(e.target.checked)}
                  />
                }
                label="Archive messages to the group"
              />
            )}
          />
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
          Settings
        </Typography>

        <Grid sx={{ p: 4 }} container spacing={4}>
          <Grid size={{ xs: 6 }}>
            <Controller
              name="membersCanPostAsTheGroup"
              control={control}
              render={({ field: props }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...props}
                      checked={props.value}
                      disabled={
                        (!userView &&
                          !isAbleToSeeUIElement(
                            directoryRoles,
                            DirectoryRoles.EditSettingsSection.value
                          )) ||
                        (userView && isUserMember)
                      }
                      onChange={(e) => props.onChange(e.target.checked)}
                    />
                  }
                  label="Allow members to post as the group."
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Controller
              name="messageModerationLevel"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'messageModerationLevel',
                  }}
                  InputLabelProps={{
                    htmlFor: 'messageModerationLevel',
                  }}
                  select
                  disabled={
                    (!userView &&
                      !isAbleToSeeUIElement(
                        directoryRoles,
                        DirectoryRoles.EditSettingsSection.value
                      )) ||
                    (userView && isUserMember)
                  }
                  label="Moderate Messages"
                  sx={{ width: 400 }}
                  value={field.value || ''}
                  onChange={field.onChange}
                >
                  {moderationLevelOptions.map((option) => (
                    <MenuItem value={option.value} key={option.displayValue}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Controller
              name="sendMessageDenyNotification"
              control={control}
              render={({ field: props }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...props}
                      checked={props.value}
                      disabled={
                        (!userView &&
                          !isAbleToSeeUIElement(
                            directoryRoles,
                            DirectoryRoles.EditSettingsSection.value
                          )) ||
                        (userView && isUserMember)
                      }
                      onChange={(e) => props.onChange(e.target.checked)}
                    />
                  }
                  label="Notify authors when moderators reject their posts."
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Controller
              name="defaultMessageDenyNotificationText"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'defaultMessageDenyNotificationText',
                  }}
                  InputLabelProps={{
                    htmlFor: 'defaultMessageDenyNotificationText',
                  }}
                  label="Rejection Message"
                  sx={{ width: 400 }}
                  disabled={
                    (!userView &&
                      !isAbleToSeeUIElement(
                        directoryRoles,
                        DirectoryRoles.EditSettingsSection.value
                      )) ||
                    (userView && isUserMember)
                  }
                  rows={3}
                  multiline
                  value={field.value || ''}
                  onChange={field.onChange}
                ></TextField>
              )}
            />
          </Grid>
          <Grid size={{ xs: 7 }}>
            <Controller
              name="spamModerationLevel"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'spamModerationLevel',
                  }}
                  InputLabelProps={{
                    htmlFor: 'spamModerationLevel',
                  }}
                  select
                  disabled={
                    (!userView &&
                      !isAbleToSeeUIElement(
                        directoryRoles,
                        DirectoryRoles.EditSettingsSection.value
                      )) ||
                    (userView && isUserMember)
                  }
                  label="Spam Messages"
                  helperText="How should suspected spam messages be handled?"
                  sx={{ width: 700 }}
                  value={field.value || ''}
                  onChange={field.onChange}
                >
                  {spamModerationLevelOptions.map((option) => (
                    <MenuItem value={option.value} key={option.displayValue}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Controller
              name="replyTo"
              control={control}
              render={({ field }) => (
                <TextField
                  inputProps={{
                    id: 'replyTo',
                  }}
                  InputLabelProps={{
                    htmlFor: 'replyTo',
                  }}
                  select
                  disabled={
                    (!userView &&
                      !isAbleToSeeUIElement(
                        directoryRoles,
                        DirectoryRoles.EditSettingsSection.value
                      )) ||
                    (userView && isUserMember)
                  }
                  label="Post Replies"
                  helperText="Replies to posts can be sent to a custom email."
                  sx={{ width: 400 }}
                  value={field.value || ''}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    if (e.target.value != 'REPLY_TO_CUSTOM') {
                      setValue('customReplyTo', '');
                    }
                  }}
                >
                  {replyToOptions.map((option) => (
                    <MenuItem value={option.value} key={option.displayValue}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
          {watch('replyTo') === 'REPLY_TO_CUSTOM' ? (
            <Grid size={{ xs: 6 }}>
              <Controller
                name="customReplyTo"
                control={control}
                rules={{
                  validate: {
                    AddressIsValid: (value) => {
                      const emailError = !emailRegex.test(value);

                      if (
                        emailError &&
                        watch('replyTo') === 'REPLY_TO_CUSTOM'
                      ) {
                        return 'Not a valid email.';
                      }
                    },
                  },
                }}
                render={({ field }) => (
                  <TextField
                    inputProps={{
                      id: 'customReplyTo',
                    }}
                    InputLabelProps={{
                      htmlFor: 'customReplyTo',
                    }}
                    label="Custom Address"
                    sx={{ width: 400 }}
                    error={!!errors.customReplyTo}
                    helperText={errors?.customReplyTo?.message || ''}
                    value={field.value || ''}
                    onChange={field.onChange}
                  />
                )}
              />
            </Grid>
          ) : null}
        </Grid>
        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
          Permissions
        </Typography>
        <Stack alignItems="center" justifyContent={'space-evenly'}>
          <Grid sx={{ p: 4 }} container spacing={4}>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="whoCanViewGroup"
                control={control}
                render={({ field }) => (
                  <TextField
                    inputProps={{
                      id: 'whoCanViewGroup',
                    }}
                    InputLabelProps={{
                      htmlFor: 'whoCanViewGroup',
                    }}
                    name="whoCanViewGroup"
                    select
                    disabled={
                      (!userView &&
                        !isAbleToSeeUIElement(
                          directoryRoles,
                          DirectoryRoles.EditSettingsSection.value
                        )) ||
                      (userView && isUserMember)
                    }
                    label="Who can view conversations"
                    sx={{ width: 400 }}
                    value={field.value || ''}
                    onChange={field.onChange}
                    helperText={
                      errors?.whoCanViewGroup?.message ||
                      'Users can view conversations in Groups if web forum is enabled.'
                    }
                    error={!!errors.whoCanViewGroup}
                  >
                    {whoCanViewGroupOptions.map((option) => (
                      <MenuItem value={option.value} key={option.displayValue}>
                        {option.displayValue}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="whoCanPostMessage"
                control={control}
                render={({ field }) => (
                  <TextField
                    inputProps={{
                      id: 'whoCanPostMessage',
                    }}
                    InputLabelProps={{
                      htmlFor: 'whoCanPostMessage',
                    }}
                    select
                    disabled={
                      (!userView &&
                        !isAbleToSeeUIElement(
                          directoryRoles,
                          DirectoryRoles.EditSettingsSection.value
                        )) ||
                      archiveOnly ||
                      (userView && isUserMember)
                    }
                    label="Who can post"
                    helperText="Users can start and participate in group conversations."
                    sx={{ width: 400 }}
                    value={field.value || ''}
                    onChange={field.onChange}
                  >
                    {whoCanPostMessageOptions.map((option) => (
                      <MenuItem value={option.value} key={option.displayValue}>
                        {option.displayValue}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="whoCanViewMembership"
                control={control}
                render={({ field }) => (
                  <TextField
                    inputProps={{
                      id: 'whoCanViewMembership',
                    }}
                    InputLabelProps={{
                      htmlFor: 'whoCanViewMembership',
                    }}
                    select
                    disabled={
                      (!userView &&
                        !isAbleToSeeUIElement(
                          directoryRoles,
                          DirectoryRoles.EditSettingsSection.value
                        )) ||
                      watch('whoCanViewGroup') === 'ANYONE_CAN_VIEW' ||
                      (userView && isUserMember)
                    }
                    label="Who can view members"
                    helperText="Users can see the members list for the group."
                    sx={{ width: 400 }}
                    value={field.value || ''}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      whoCanViewMemberOnchange(e);
                    }}
                  >
                    {whoCanViewMembersOptions.map((option) => (
                      <MenuItem value={option.value} key={option.displayValue}>
                        {option.displayValue}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="whoCanModerateMembers"
                control={control}
                render={({ field }) => (
                  <TextField
                    inputProps={{
                      id: 'whoCanModerateMembers',
                    }}
                    InputLabelProps={{
                      htmlFor: 'whoCanModerateMembers',
                    }}
                    select
                    disabled={
                      (!userView &&
                        !isAbleToSeeUIElement(
                          directoryRoles,
                          DirectoryRoles.EditSettingsSection.value
                        )) ||
                      (userView && isUserMember)
                    }
                    label="Who can manage members"
                    helperText="Control who is allowed to add and remove group members."
                    sx={{ width: 400 }}
                    value={field.value || ''}
                    onChange={field.onChange}
                  >
                    {getFilteredModerateOptions(whoCanViewMembershipValue).map(
                      (option) => (
                        <MenuItem
                          value={option.value}
                          key={option.displayValue}
                        >
                          {option.displayValue}
                        </MenuItem>
                      )
                    )}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="whoCanModerateContent"
                control={control}
                render={({ field }) => (
                  <TextField
                    inputProps={{
                      id: 'whoCanModerateContent',
                    }}
                    InputLabelProps={{
                      htmlFor: 'whoCanModerateContent',
                    }}
                    select
                    disabled={
                      (!userView &&
                        !isAbleToSeeUIElement(
                          directoryRoles,
                          DirectoryRoles.EditSettingsSection.value
                        )) ||
                      (userView && isUserMember)
                    }
                    label="Who can moderate content"
                    helperText="This includes approving, locking, and deleting messages and conversations."
                    sx={{ width: 400 }}
                    value={field.value || ''}
                    onChange={field.onChange}
                  >
                    {whoCanModerateContentOptions.map((option) => (
                      <MenuItem value={option.value} key={option.displayValue}>
                        {option.displayValue}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="whoCanContactOwner"
                control={control}
                render={({ field }) => (
                  <TextField
                    inputProps={{
                      id: 'whoCanContactOwner',
                    }}
                    InputLabelProps={{
                      htmlFor: 'whoCanContactOwner',
                    }}
                    select
                    disabled={
                      (!userView &&
                        !isAbleToSeeUIElement(
                          directoryRoles,
                          DirectoryRoles.EditSettingsSection.value
                        )) ||
                      (userView && isUserMember)
                    }
                    label="Who can contact group owners"
                    helperText="Control who can send messages to group owners"
                    sx={{ width: 400 }}
                    value={field.value || ''}
                    onChange={field.onChange}
                  >
                    {whoCanContactOwnerOptions.map((option) => (
                      <MenuItem value={option.value} key={option.displayValue}>
                        {option.displayValue}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="whoCanAssistContent"
                control={control}
                render={({ field }) => (
                  <TextField
                    inputProps={{
                      id: 'whoCanAssistContent',
                    }}
                    InputLabelProps={{
                      htmlFor: 'whoCanAssistContent',
                    }}
                    select
                    disabled={
                      (!userView &&
                        !isAbleToSeeUIElement(
                          directoryRoles,
                          DirectoryRoles.EditSettingsSection.value
                        )) ||
                      (userView && isUserMember)
                    }
                    label="Who can assist content"
                    helperText="Specifies who can moderate metadata"
                    sx={{ width: 400 }}
                    value={field.value || ''}
                    onChange={field.onChange}
                  >
                    {whoCanAssistContentOptions.map((option) => (
                      <MenuItem value={option.value} key={option.displayValue}>
                        {option.displayValue}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="whoCanLeaveGroup"
                control={control}
                render={({ field }) => (
                  <TextField
                    inputProps={{
                      id: 'whoCanLeaveGroup',
                    }}
                    InputLabelProps={{
                      htmlFor: 'whoCanLeaveGroup',
                    }}
                    select
                    disabled={
                      (!userView &&
                        !isAbleToSeeUIElement(
                          directoryRoles,
                          DirectoryRoles.EditSettingsSection.value
                        )) ||
                      (userView && isUserMember)
                    }
                    label="Who can leave group"
                    helperText="Specifies who can leave the group"
                    sx={{ width: 400 }}
                    value={field.value || ''}
                    onChange={field.onChange}
                  >
                    {whoCanLeaveGroupOptions.map((option) => (
                      <MenuItem value={option.value} key={option.displayValue}>
                        {option.displayValue}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="whoCanDiscoverGroup"
                control={control}
                render={({ field }) => (
                  <TextField
                    inputProps={{
                      id: 'whoCanDiscoverGroup',
                    }}
                    InputLabelProps={{
                      htmlFor: 'whoCanDiscoverGroup',
                    }}
                    select
                    disabled={
                      (!userView &&
                        !isAbleToSeeUIElement(
                          directoryRoles,
                          DirectoryRoles.EditSettingsSection.value
                        )) ||
                      (userView && isUserMember)
                    }
                    label="Who can discover group"
                    helperText="Specifies the set of users for whom this group is discoverable"
                    sx={{ width: 400 }}
                    value={field.value || ''}
                    onChange={field.onChange}
                  >
                    {getFilteredDiscoverOptions(whoCanViewMembershipValue).map(
                      (option) => (
                        <MenuItem
                          value={option.value}
                          key={option.displayValue}
                        >
                          {option.displayValue}
                        </MenuItem>
                      )
                    )}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="whoCanJoin"
                control={control}
                render={({ field }) => (
                  <TextField
                    inputProps={{
                      id: 'whoCanJoin',
                    }}
                    InputLabelProps={{
                      htmlFor: 'whoCanJoin',
                    }}
                    name="whoCanJoin"
                    select
                    disabled={
                      (!userView &&
                        !isAbleToSeeUIElement(
                          directoryRoles,
                          DirectoryRoles.EditSettingsSection.value
                        )) ||
                      (userView && isUserMember)
                    }
                    label="Who can join group"
                    sx={{ width: 400 }}
                    value={field.value || ''}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      if (e.target.value === 'ANYONE_CAN_JOIN') {
                        setValue('allowExternalMembers', true);
                      }
                    }}
                    error={!!errors.whoCanJoin}
                    helperText={
                      errors?.whoCanJoin?.message ||
                      'Control who can join the group'
                    }
                  >
                    {inviteMembersOptions.map((option) => (
                      <MenuItem value={option.value} key={option.displayValue}>
                        {option.displayValue}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <Controller
                name="allowExternalMembers"
                control={control}
                render={({ field: props }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...props}
                        disabled={
                          (!userView &&
                            !isAbleToSeeUIElement(
                              directoryRoles,
                              DirectoryRoles.EditSettingsSection.value
                            )) ||
                          watch('whoCanJoin') === 'ANYONE_CAN_JOIN' ||
                          (userView && isUserMember)
                        }
                        checked={props.value}
                        onChange={(e) => props.onChange(e.target.checked)}
                      />
                    }
                    label={`Allow new users not in ${domain}`}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Typography sx={{ fontSize: 14, fontWeight: 300, marginTop: 2 }}>
          Advanced
        </Typography>
        <Stack sx={{ py: 2, px: 4 }} gap={2}>
          <Typography variant="body2" sx={{ fontWeight: 300 }}>
            {getValues('archiveOnly')
              ? `This group is currently disabled. All new posts to the group will be rejected. Existing messages are viewable and searchable online by anyone that already has the permission to view the group's content. If the group is re-enabled, the setting for 'Post Options' will automatically be set to 'Managers of the group.'`
              : `This group is currently enabled. If you disable it all new posts to the group will be rejected. Existing messages will be viewable and searchable online by anyone that already has the permission to view the group's content. If the group is re-enabled, the setting for 'Post Options' will automatically be set to 'Managers of the group.'`}
          </Typography>
          <Box>
            <Button
              variant="contained"
              loading={updatingGroupSettings || updatingGroupSettingsUserPortal}
              disabled={
                (!userView &&
                  !isAbleToSeeUIElement(
                    directoryRoles,
                    DirectoryRoles.EditSettingsSection.value
                  )) ||
                (userView && isUserMember)
              }
              onClick={() => {
                if (
                  !userView &&
                  isAbleToSeeUIElement(
                    changeLogRoles,
                    DirectoryRoles.EditAdvancedSection.value
                  )
                ) {
                  setEnableDisableGroupClicked(true);
                  setOpenChangeLogDialog(true);
                } else {
                  if (getValues('archiveOnly')) {
                    const newSettings: GroupBusinessSettings = {
                      ...groupSettings,
                      whoCanPostMessage: 'ALL_MANAGERS_CAN_POST',
                      archiveOnly: false,
                    };
                    if (userView) {
                      userPortalMutate(newSettings);
                    } else {
                      mutate(newSettings);
                    }
                  } else {
                    const newSettings: GroupBusinessSettings = {
                      ...groupSettings,
                      whoCanPostMessage: 'NONE_CAN_POST',
                      archiveOnly: true,
                    };
                    if (userView) {
                      userPortalMutate(newSettings);
                    } else {
                      mutate(newSettings);
                    }
                  }
                }
              }}
            >
              {getValues('archiveOnly') ? 'Enable Group' : 'Disable Group'}
            </Button>
          </Box>
        </Stack>
      </Stack>

      <ChangeLogDialog
        open={openChangeLogDialog}
        handleClose={() => setOpenChangeLogDialog(false)}
        changeLogMessage={changeLogMessage}
        setChangeLogMessage={setChangeLogMessage}
        mutate={() => {
          if (enableDisableGroupClicked) {
            if (getValues('archiveOnly')) {
              const newSettings: GroupBusinessSettings = {
                ...groupSettings,
                whoCanPostMessage: 'ALL_MANAGERS_CAN_POST',
                archiveOnly: false,
              };
              mutate(newSettings);
            } else {
              const newSettings: GroupBusinessSettings = {
                ...groupSettings,
                whoCanPostMessage: 'NONE_CAN_POST',
                archiveOnly: true,
              };
              mutate(newSettings);
            }
          } else {
            const newSettings: GroupBusinessSettings = {
              ...groupSettings,
              ...getValues(),
            };
            mutate(newSettings);
          }
        }}
        mutateLoading={updatingGroupSettings}
      />
    </Box>
  ) : (
    <Alert severity="error">
      You must be a manager, owner, or a member of the group to be able to view
      the group settings.
    </Alert>
  );
}
